import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { useMemo } from 'react'

import { computeRangePreset, useShoppingListQuery, ymdUtc } from '@/api/shopping-list'
import { useSubstitutionRequestsQuery } from '@/api/substitution-requests'
import { type IngredientOption, SubstitutionModalForm } from '@/components/chefs/substitution-modal-form'
import { SubstitutionModalLedger } from '@/components/chefs/substitution-modal-ledger'
import { useActiveKitchen } from '@/hooks/chefs/useActiveKitchen'
import { useSubstitutionModal } from '@/hooks/chefs/useSubstitutionModal'
import { toUiSubstitutions } from '@/lib/chefs/substitution-adapters'
import { ingredientKeyFor } from '@/lib/chefs/substitutions'

export function SubstitutionModal() {
  const kitchenLocationId = useActiveKitchen()
  const open = useSubstitutionModal((s) => s.open)
  const view = useSubstitutionModal((s) => s.view)
  const prefillName = useSubstitutionModal((s) => s.prefillIngredientName)
  const prefillUnit = useSubstitutionModal((s) => s.prefillIngredientUnit)
  const showConfirmation = useSubstitutionModal((s) => s.showSubmittedConfirmation)
  const closeModal = useSubstitutionModal((s) => s.closeModal)
  const setView = useSubstitutionModal((s) => s.setView)
  const markConfirmationShown = useSubstitutionModal((s) => s.markConfirmationShown)
  const openModal = useSubstitutionModal((s) => s.openModal)

  const subsQuery = useSubstitutionRequestsQuery(
    { kitchenLocationId: kitchenLocationId ?? '' },
    { enabled: Boolean(kitchenLocationId) && open },
  )
  const scenarioSubs = useMemo(() => toUiSubstitutions(subsQuery.data?.requests ?? []), [subsQuery.data])

  // Ingredient picker options come from the REAL shopping-list
  // aggregation (this week + next — the substitution window chefs
  // actually plan against), not the mock campus recipes.
  const ingredientRange = useMemo(() => {
    const preset = computeRangePreset('this-week-plus-next', new Date())
    return { start: ymdUtc(preset.start), end: ymdUtc(preset.end) }
  }, [])
  const ingredientsQuery = useShoppingListQuery(
    { kitchenLocationId: kitchenLocationId ?? '', start: ingredientRange.start, end: ingredientRange.end },
    { enabled: Boolean(kitchenLocationId) && open },
  )
  const ingredientOptions = useMemo<IngredientOption[]>(
    () =>
      (ingredientsQuery.data?.rows ?? []).map((row) => ({
        key: ingredientKeyFor(row.ingredient.name, row.totalRawUnit),
        name: row.ingredient.name,
        unit: row.totalRawUnit,
      })),
    [ingredientsQuery.data],
  )

  const lockedIngredient = useMemo<IngredientOption | null>(() => {
    if (!prefillName || !prefillUnit) {
      return null
    }
    return {
      key: ingredientKeyFor(prefillName, prefillUnit),
      name: prefillName,
      unit: prefillUnit,
    }
  }, [prefillName, prefillUnit])

  const today = useMemo(() => new Date(), [])
  const defaultEnd = useMemo(() => {
    const d = new Date(today)
    d.setDate(d.getDate() + 6)
    return d
  }, [today])

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : closeModal())}>
      <DialogContent className="flex flex-col gap-4 sm:max-w-2xl" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{view === 'ledger' ? 'Substitutions' : 'Request a substitution'}</DialogTitle>
        </DialogHeader>

        {view === 'ledger' ? (
          <SubstitutionModalLedger
            substitutions={scenarioSubs}
            showSubmittedConfirmation={showConfirmation}
            onDismissConfirmation={markConfirmationShown}
            onRequestNew={() => setView('form')}
          />
        ) : (
          <SubstitutionModalForm
            kitchenLocationId={kitchenLocationId}
            ingredientOptions={ingredientOptions}
            lockedIngredient={lockedIngredient}
            defaultStartDate={today}
            defaultEndDate={defaultEnd}
            existingSubstitutions={scenarioSubs}
            onSeeAll={() => setView('ledger')}
            onCancel={closeModal}
            onSubmitted={() =>
              openModal({
                view: 'ledger',
                showSubmittedConfirmation: true,
              })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
